<template>
    <div>
        <v-container fluid>
            <v-row no-gutters class="d-flex justify-center align-center">
                <v-col cols="3" class="d-flex flex-row" v-if="!miscellaneousMode">
                    <oss-text-field-filled
                        append-inner-icon="$euro"
                        v-model="docFilter.amount.minimum"
                        label="Min"
                        class="amount-filter margin"
                    />
                    <oss-text-field-filled
                        append-inner-icon="$euro"
                        v-model="docFilter.amount.maximum"
                        label="Max"
                        class="amount-filter margin ml-1"
                    />
                </v-col>
                <v-col cols="3">
                    <oss-date-picker-filled
                        multiple="range"
                        :begin="docFilter.date.begin"
                        :end="docFilter.date.end"
                        :dense="true"
                        class="ml-1"
                        hide-details
                        @change-dateRange="onDocDateRangeChanged"
                    />
                </v-col>
                <v-col cols="4" v-if="!thirdPartyCompanyId">
                    <v-text-field-filled
                        v-model="docFilter.search"
                        append-inner-icon="$search"
                        label="Recherche"
                        class="ml-1"
                    />
                </v-col>
                <v-col cols="1">
                    <v-btn-icon
                        class="ml-1"
                        :disabled="isFiltered(defaultDocFilter, docFilter)"
                        @click.stop="clearDocFilters()"
                        icon="$delete"
                    />
                </v-col>
            </v-row>
        </v-container>
        <v-container fluid>
            <v-data-table-server
                :headers="tableHeaders"
                :items="tableItems"
                v-model:expanded="documentExpanded"
                v-model:page="docFilter.pagination.page"
                v-model:sort-by="docFilter.pagination.sortBy"
                v-model:sort-desc="docFilter.pagination.descending"
                v-model:items-per-page="docFilter.pagination.rowsPerPage"
                :items-length="docFilter.pagination.totalItems"
                :items-per-page-options="[5, 10, 25]"
                class="table"
                fixed-header
                max-height="60vh"
            >
                <template v-slot:item="props">
                    <tr>
                        <td class="text-left">{{ props.item.name }}</td>
                        <td class="text-center">{{ props.item.date ? props.item.date : "-" }}</td>
                        <td v-if="!miscellaneousMode" class="text-center">
                            {{
                                props.item.debit
                                    ? newCommonFormatCurrency(props.item.debit, undefined)
                                    : "-"
                            }}
                        </td>
                        <td v-if="!miscellaneousMode" class="text-center">
                            {{
                                props.item.credit
                                    ? newCommonFormatCurrency(props.item.credit, undefined)
                                    : "-"
                            }}
                        </td>
                        <td colspan="2" class="text-center">
                            <v-row no-gutters>
                                <v-col cols="12" class="d-flex justify-center align-center">
                                    <v-btn
                                        v-if="props.item.signedUrl"
                                        @click.stop="
                                            addRemoveDocumentExpand(props.item);
                                            props.toggleExpand(props.internalItem);
                                        "
                                        icon="$oss-eye"
                                        size="x-small"
                                    />
                                    <v-btn
                                        v-if="!props.item.selected"
                                        rounded
                                        class="btn ml-1"
                                        dense
                                        @click.stop="
                                            miscellaneousMode
                                                ? addDocument(props.item)
                                                : addAccountingLine(props.item)
                                        "
                                        size="small"
                                    >
                                        Ajouter
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </td>
                    </tr>
                </template>
                <template v-slot:expanded-row="{ columns, item }">
                    <td :colspan="columns.length">
                        <v-row no-gutters align="center" justify="center" v-if="item.signedUrl">
                            <v-img
                                :src="item.signedUrl"
                                lazy-src="../assets/logo.png"
                                max-width="50vw"
                                :contain="true"
                                class="elevation-5 my-4"
                            />
                        </v-row>
                    </td>
                </template>
            </v-data-table-server>
        </v-container>
    </div>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";

import { User } from "../store/store";

import { getUserDisplayName } from "../services/userServices";
import { getStringAsFloat } from "../services/mathServices";
import { newCommonFormatCurrency } from "../../../backend/src/common/services/mathServices";
import { getDBFormattedEndDate, getDBFormattedStartDate } from "../services/dateServices";

import moment from "moment-timezone";

import RECONCILIATION_ACCOUNTING_LINE_QUERY from "../graphql/reconciliation/ReconciliationAccountingLines.gql";
import MISCELLANEOUS_DOCS_QUERY from "../graphql/document/MiscellaneousDocuments.gql";
import { Component, Vue, toNative, Prop, Watch } from "vue-facing-decorator";

@Component({
    emits: ["addAccountingLine", "addDocument"],
})
export class DocumentReconciliator extends Vue {
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: null }) reconciliationItems: any;
    @Prop({ default: null }) itemReconciliations: any;
    @Prop({ default: 0.0 }) amountSigned: number;
    @Prop({ default: false }) miscellaneousMode: boolean;
    @Prop({ default: "" }) documentType: string;
    @Prop({ default: "" }) thirdPartyCompanyId: string;

    selectedGroup = null;

    documentExpanded = [];

    selectedItems = [];

    defaultDocFilter = {
        search: "",
        showFilterBox: false,
        amount: {
            maximum: "",
            minimum: "",
        },
        date: {
            begin: "",
            end: "",
            format: "YYYY-MM-DD",
        },
        pagination: {
            sortBy: [{ key: "wording" }],
            descending: false,
            page: 1,
            rowsPerPage: 5,
            totalItems: 0,
        },
    };

    docFilter: any = {
        search: "",
        showFilterBox: false,
        amount: {
            maximum: "",
            minimum: "",
        },
        date: {
            begin: "",
            end: "",
            format: "YYYY-MM-DD",
        },
        pagination: {
            sortBy: [{ key: "wording" }],
            descending: false,
            page: 1,
            rowsPerPage: 5,
            totalItems: 0,
        },
    };

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    @Watch("docFilter.search")
    @Watch("docFilter.amount.minimum")
    @Watch("docFilter.amount.maximum")
    @Watch("docFilter.date.begin")
    @Watch("docFilter.date.end")
    onDocumentFilterChanged() {
        this.docFilter.pagination.page = 1;
        this.accountingLines = [];
    }

    get tableItems() {
        return this.miscellaneousMode ? this.miscellaneousDocs : this.accountingLines;
    }

    get tableHeaders(): any[] {
        if (this.miscellaneousMode) {
            return [
                { title: "Nom", value: "name", sortable: false, align: "left" },
                { title: "Date d'import", value: "date", sortable: false, align: "center" },
                { title: "Action", value: "action", sortable: false, align: "center" },
            ];
        }
        return [
            { title: "Nom", value: "name", sortable: false, align: "left" },
            { title: "Date", value: "date", sortable: false, align: "center" },
            { title: "Débit", value: "debit", sortable: false, align: "center" },
            { title: "Crédit", value: "credit", sortable: false, align: "center" },
            { title: "Action", value: "action", sortable: false, align: "center" },
        ];
    }

    get isAccountingUser() {
        return this.currentUser.isAccountingUser;
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get selectedCompany() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get reconciliationAccountingLineWhere() {
        const where: any = {
            companyId: this.selectedCompany.id,
            q: this.docFilter.search.length > 0 ? this.docFilter.search : undefined,
            qInt: this.getDocQInt(),
            thirdPartyCompany: this.docFilter.search.length
                ? { name_contains: this.docFilter.search }
                : undefined,
            minAmount: this.amountSigned,
            maxAmount: this.amountSigned,
            beginDate: getDBFormattedStartDate(this.docFilter.date.begin),
            endDate: getDBFormattedEndDate(this.docFilter.date.end),
            first:
                this.docFilter.pagination.rowsPerPage > 0
                    ? this.docFilter.pagination.rowsPerPage
                    : 100,
            skip: this.docFilter.pagination.rowsPerPage * (this.docFilter.pagination.page - 1),
            orderBy: this.formatOrderByDoc(),
            documentType: this.documentType,
        };

        if (this.thirdPartyCompanyId) {
            where.thirdPartyCompanyId = { id: this.thirdPartyCompanyId };
        }

        const btAccountingLine =
            this.selectedGroup && this.selectedGroup.items && this.selectedGroup.items.length
                ? this.selectedGroup.items[0].accountingLine
                : undefined;
        if (btAccountingLine) {
            if (btAccountingLine.user) {
                where.user = { id: btAccountingLine.user.id };
            }
            if (btAccountingLine.thirdPartyCompany) {
                where.thirdPartyCompanyId = { id: btAccountingLine.thirdPartyCompany.id };
            }
            if (
                btAccountingLine.accountableAccount &&
                !btAccountingLine.accountableAccount.name.startsWith("471000")
            ) {
                where.accountableAccount = { name: btAccountingLine.accountableAccount.name };
            }
        }
        return where;
    }

    get miscellaneousDocumentsWhere() {
        const where: any = {
            companyId: this.selectedCompany.id,
            q: this.docFilter.search.length > 0 ? this.docFilter.search : undefined,
            first:
                this.docFilter.pagination.rowsPerPage > 0
                    ? this.docFilter.pagination.rowsPerPage
                    : 100,
            skip: this.docFilter.pagination.rowsPerPage * (this.docFilter.pagination.page - 1),
            orderBy: this.formatOrderByDoc(),
        };

        return where;
    }

    @Apollo({
        query() {
            return RECONCILIATION_ACCOUNTING_LINE_QUERY;
        },
        variables() {
            return this.reconciliationAccountingLineWhere;
        },
        debounce: 500,
        skip() {
            return this.miscellaneousMode;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                this.accountingLines = [];
                this.documentExpanded = [];
                if (data?.accountingLines) {
                    for (const al of data.accountingLines) {
                        let selected = false;
                        if (this.reconciliationItems) {
                            selected = !!this.reconciliationItems.find(
                                (item) => item.accountingLine && item.accountingLine.id === al.id,
                            );
                        }
                        const item = this.getDocAccountingLineItem(al, selected, false);
                        this.accountingLines.push(item);
                    }
                    if (data.accountingLinesConnection) {
                        this.docFilter.pagination.totalItems =
                            data.accountingLinesConnection.aggregate.count;
                    } else {
                        debugger;
                        this.docFilter.pagination.totalItems = 0;
                    }
                }
            }
        },
    })
    accountingLines = [];

    @Apollo({
        query() {
            return MISCELLANEOUS_DOCS_QUERY;
        },
        variables() {
            return this.miscellaneousDocumentsWhere;
        },
        debounce: 500,
        skip() {
            return !this.miscellaneousMode;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                this.miscellaneousDocs = [];
                this.documentExpanded = [];
                if (data?.documents) {
                    for (const doc of data.documents) {
                        doc.selected = false;
                        doc.date = this.formatDate(doc.createdAt);
                        this.miscellaneousDocs.push(doc);
                    }
                    this.docFilter.pagination.totalItems = data.documents.length || 0;
                }
            }
        },
    })
    miscellaneousDocs = [];

    getDocAccountingLineItem(accountingLine, selected: boolean, initial: boolean) {
        const doc = accountingLine.document;
        const date = accountingLine.date
            ? moment(accountingLine.date).format("DD/MM/YYYY")
            : doc.date
              ? moment(doc.date).format("DD/MM/YYYY")
              : "-";
        const invoiceNumber =
            doc && doc.type === "SALES_INVOICE" && doc.invoiceNumber
                ? " - " + doc.invoiceNumber
                : "";
        const item = {
            accountingLine,
            selected,
            initial,
            doc,
            id: doc ? doc.id : "",
            type: "Document",
            iconType: "$receipt",
            name: accountingLine.wording + invoiceNumber,
            date,
            credit: 0.0,
            debit: 0.0,
            signedUrl: "",
            //
            // see how to get accountableAccount
            //
            accountableAccountAuto: null,
            accountableAccount: null,

            accountableAccountSuggestions: [],
            // subCreditTotal: 0.0,
            // subDebitTotal: 0.0,
            // credit_1: 0.0,
            // debit_1: 0.0,
            // accountableAccount1: undefined,
            // validated_1: false,
            // credit_2: 0.0,
            // debit_2: 0.0,
            // accountableAccount2: undefined,
            // validated_2: false,
            amountSigned: accountingLine.amountSigned,
            // netAmount: 0.0,
            // netAmount_1: 0.0,
            // netAmount_2: 0.0
            suppliers: "",
            customers: "",
            user: "",
            bankAccount: "",
            editable: true,
        };

        if (
            accountingLine &&
            accountingLine.reconciliation &&
            accountingLine.reconciliation.accountingItemImport
        ) {
            item.editable = false;
        }

        this.computeDisabledAccountableAccount(item);
        // sub value
        this.updateDebitCredit(item);
        // if (accountingLine.amountSigned < 0) {
        //     item.debit = -accountingLine.amountSigned;
        //     item.subDebitTotal = doc && doc.netAmount !== undefined ? doc.netAmount : -accountingLine.amountSigned;
        // } else {
        //     item.credit = accountingLine.amountSigned;
        //     item.subCreditTotal = doc && doc.netAmount !== undefined ? doc.netAmount : accountingLine.amountSigned;
        // }
        if (doc) {
            // item.accountableAccountAuto = doc.accountableAccountAuto
            // item.accountableAccount = doc.accountableAccount
            if (doc.signedUrl) {
                item.signedUrl = doc.signedUrl;
            }
        }
        this.updateThirdParty(item);

        return item;
    }

    selectAllItems() {
        if (this.selectedItems.length === this.tableItems.length) {
            this.selectedItems = [];
        } else {
            this.selectedItems = this.tableItems;
        }
    }

    emptyFunction() {}

    addAccountingLine(item) {
        this.$emit("addAccountingLine", item);
    }

    addDocument(item) {
        this.$emit("addDocument", item);
    }

    computeReconciliations(item) {
        if (this.itemReconciliations && item.accountingLine.reconciliation) {
            if (this.itemReconciliations[item.accountingLine.reconciliation.id]) {
                item.disabledAccountableAccount = true;
                this.itemReconciliations[
                    item.accountingLine.reconciliation.id
                ].accountingLines.push(item.accountingLine);
            } else {
                this.itemReconciliations[item.accountingLine.reconciliation.id] = {
                    id: item.accountingLine.reconciliation.id,
                    accountingLines: [item.accountingLine],
                };
            }
        }
    }

    computeDisabledAccountableAccount(item) {
        this.computeReconciliations(item);
        if (item.disabledAccountableAccount) {
            return;
        }
        if (item.doc && item.doc.acountingItemImport) {
            item.disabledAccountableAccount = true;
            return;
        }
        if (item.bt && item.bt.acountingItemImport) {
            item.disabledAccountableAccount = true;
            return;
        }
        if (!this.isAccountingUser) {
            item.disabledAccountableAccount = true;
            return;
        }

        if (item.accountingLine) {
            if (item.accountingLine.accountableAccount) {
                if (
                    item.accountingLine.accountableAccount.name.startsWith("531000") ||
                    item.accountingLine.accountableAccount.name.startsWith("512") ||
                    item.accountingLine.accountableAccount.name === "INTERNAL_LINK" ||
                    item.accountingLine.accountableAccount.name.startsWith("511000") ||
                    (item.accountingLine.accountableAccount.name.startsWith("421000") &&
                        item.accountingLine.reconciliation) ||
                    item.accountingLine.accountableAccount.name.startsWith("580") ||
                    item.accountingLine.accountableAccount.name.startsWith("511") ||
                    item.accountingLine.accountableAccount.name.startsWith("455000") ||
                    item.accountingLine.accountableAccount.name.startsWith("467000") ||
                    item.accountingLine.accountableAccount.name.startsWith("108000")
                ) {
                    item.disabledAccountableAccount = true;
                }
            }
        }
    }

    updateThirdParty(item) {
        item.suppliers = "";
        item.customers = "";
        item.user = "";
        item.bankAccount = "";
        if (item.accountingLine) {
            const al = item.accountingLine;
            if (al.thirdPartyCompany) {
                if (item.accountingLine.accountableAccount.name.startsWith("401")) {
                    item.suppliers = item.suppliers ? (item.suppliers += " - ") : "";
                    item.suppliers += al.thirdPartyCompany.shortName || al.thirdPartyCompany.name;
                } else if (item.accountingLine.accountableAccount.name.startsWith("411")) {
                    item.customers = item.customers ? (item.customers += " - ") : "";
                    item.customers += al.thirdPartyCompany.shortName || al.thirdPartyCompany.name;
                }
            }
            if (al.user) {
                item.user = item.user ? (item.user += " - ") : "";
                item.user += getUserDisplayName(al.user);
            }
            if (al.bankAccount) {
                item.bankAccount += al.bankAccount.name;
            }
        }
    }

    updateDebitCredit(item) {
        if (item.amountSigned >= 0) {
            item.debit = item.amountSigned;
        } else {
            item.credit = -item.amountSigned;
        }
    }

    formatOrderByDoc() {
        let direction = "";
        let fieldName = "";
        if (this.miscellaneousMode) {
            fieldName = "name";
            if (fieldName !== "updatedAt") {
                direction = this.docFilter.pagination.descending ? "_DESC" : "_ASC";
            } else {
                direction = this.docFilter.pagination.descending ? "_ASC" : "_DESC";
            }
        } else {
            fieldName = this.docFilter.pagination.sortBy[0]?.key || "name";
            if (fieldName !== "updatedAt") {
                direction = this.docFilter.pagination.descending ? "_DESC" : "_ASC";
            } else {
                direction = this.docFilter.pagination.descending ? "_ASC" : "_DESC";
            }
        }
        return fieldName + direction;
    }

    getDocQInt() {
        let qInt = getStringAsFloat(this.docFilter.search);
        if (qInt !== undefined) {
            qInt = Math.abs(qInt);
        }
        return qInt;
    }

    addRemoveDocumentExpand(item) {
        const index = this.documentExpanded.indexOf(item);
        if (index === -1) {
            this.documentExpanded.push(item);
        } else {
            this.documentExpanded.splice(index, 1);
        }
        item.isExpanded = !item.isExpanded;
    }

    isFiltered(x: any, y: any): boolean {
        const optFields = ["showFilterBox", "totalItems"];
        if (x && y && typeof x === "object" && typeof y === "object") {
            return (
                Object.keys(x).length === Object.keys(y).length &&
                Object.keys(x).reduce((isEqual, key) => {
                    if (optFields.includes(key)) {
                        return isEqual;
                    }
                    return isEqual && this.isFiltered(x[key], y[key]);
                }, true)
            );
        }
        return x === y;
    }

    clearDocFilters() {
        this.docFilter = JSON.parse(JSON.stringify(this.defaultDocFilter));
    }

    onDocDateRangeChanged(data) {
        this.docFilter.date.begin = data.begin;
        this.docFilter.date.end = data.end;
    }

    formatDate(date) {
        return moment(date).format("DD/MM/YYYY");
    }
}

export default toNative(DocumentReconciliator);
</script>
